import { Instagram, Mail, Phone, MapPin, Clock, ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/Motion";
import { BUSINESS } from "@/lib/site";
import usePageTitle from "@/lib/usePageTitle";

export default function Contact() {
  usePageTitle("Contact Us — Skies The Limit Creations LLC");

  return (
    <div className="grain">
      <header className="relative overflow-hidden pb-8 pt-[130px] lg:pt-[160px]">
        <div aria-hidden className="pointer-events-none absolute -top-32 right-[-10%] h-[420px] w-[420px] rounded-full bg-ember/15 blur-[130px]" />
        <div className="mx-auto max-w-7xl px-5 sm:px-8" data-testid="contact-header">
          <Reveal>
            <p className="eyebrow mb-5">Contact</p>
            <h1 className="max-w-3xl font-display text-4xl font-extrabold tracking-tight text-white sm:text-5xl lg:text-6xl">
              Let's talk about your idea.
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-mist sm:text-lg">
              Call, text, email, or DM — we're a small family shop, and real people answer.
            </p>
          </Reveal>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-5 pb-24 sm:px-8 lg:pb-32">
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="contact-cards">
          <Reveal>
            <a
              href={`mailto:${BUSINESS.email}`}
              data-testid="contact-email-card"
              className="card-panel group flex h-full flex-col justify-between p-8 transition-colors duration-300 hover:border-gold/40"
            >
              <Mail className="h-7 w-7 text-ember" aria-hidden />
              <div className="mt-10">
                <p className="eyebrow mb-2">Email</p>
                <p className="break-all text-sm font-semibold text-white">{BUSINESS.email}</p>
              </div>
              <span className="mt-5 inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-gold">
                Write to us
                <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" aria-hidden />
              </span>
            </a>
          </Reveal>

          <Reveal delay={0.1}>
            <a
              href={BUSINESS.phoneHref}
              data-testid="contact-phone-card"
              className="card-panel group flex h-full flex-col justify-between p-8 transition-colors duration-300 hover:border-gold/40"
            >
              <Phone className="h-7 w-7 text-ember" aria-hidden />
              <div className="mt-10">
                <p className="eyebrow mb-2">Call or text</p>
                <p className="text-sm font-semibold text-white">{BUSINESS.phoneDisplay}</p>
              </div>
              <span className="mt-5 inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-gold">
                Reach us directly
                <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" aria-hidden />
              </span>
            </a>
          </Reveal>

          <Reveal delay={0.2}>
            <a
              href={BUSINESS.instagram.href}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-instagram-card"
              className="card-panel group flex h-full flex-col justify-between p-8 transition-colors duration-300 hover:border-gold/40"
            >
              <Instagram className="h-7 w-7 text-ember" aria-hidden />
              <div className="mt-10">
                <p className="eyebrow mb-2">Instagram</p>
                <p className="text-sm font-semibold text-white">{BUSINESS.instagram.label}</p>
              </div>
              <span className="mt-5 inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-gold">
                See new work
                <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" aria-hidden />
              </span>
            </a>
          </Reveal>
        </div>

        <div className="mt-6 grid gap-5 sm:grid-cols-2" data-testid="contact-placeholders">
          <Reveal>
            <div className="placeholder-frame min-h-[160px] p-8" data-testid="contact-address-placeholder">
              <MapPin className="h-6 w-6 text-gold/70" aria-hidden />
              <p className="font-display text-base font-bold text-white">Business address</p>
              <p className="text-xs text-mist">[Add your address or service area here]</p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="placeholder-frame min-h-[160px] p-8" data-testid="contact-hours-placeholder">
              <Clock className="h-6 w-6 text-gold/70" aria-hidden />
              <p className="font-display text-base font-bold text-white">Business hours</p>
              <p className="text-xs text-mist">[Add your hours here]</p>
            </div>
          </Reveal>
        </div>

        <Reveal>
          <p className="mt-8 text-center text-xs text-mist/70" data-testid="contact-verified-note">
            Only verified links and numbers are shown. Have another profile? Send it over and we'll add it.
          </p>
        </Reveal>
      </main>
    </div>
  );
}
